/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flatten from 'lodash.flatten';

export const oxfordJoin = (arr: string[], conjunction = 'and', ifEmpty = ''): string => {
    switch (arr.length) {
        case 0:
            return ifEmpty;
        case 1:
            return arr[0];
        case 2:
            return `${arr[0]} ${conjunction} ${arr[1]}`;
        default:
            return arr
                .slice(0, -1)
                .concat([`${conjunction} ${arr[arr.length - 1]}`])
                .join(', ');
    }
};

/**
 * Deep maps and flattens the array recursively based
 * on the current iteratee. Uses BFS
 *
 * @example
 * Input:
 * [
 *      {
 *          id: 'Parent',
 *          children: [
 *              {
 *                  id: 'Son' ,
 *                  children: [{ id: 'GrandSon' }]
 *              },
 *              {
 *                  id: 'Daughter' ,
 *                  children: [{ id: 'GrandDaughter' }]
 *              },
 *          ]
 *      }
 * ]
 * Iteratee: item => item.children
 *
 * Output:
 * [{ id: 'Parent' }, { id: 'Son' }, { id: 'Daughter' }, { id: 'GrandSon' }, { id: 'GrandDaughter' }]
 *
 * Note: I omitted the children array from the objects for brevity
 *
 * @param array The array to flatten
 * @param iteratee The method to apply each array level.
 */
export const flatMapDeep = <T>(array: T[], iteratee: (value: T) => T[]): T[] => {
    if (!array || array.length === 0) {
        return [];
    }

    return array.concat(flatMapDeep(flatten(array.map(iteratee)), iteratee));
};
